import { Button } from 'alurkerja-ui';
import { LucideDownload, LucideFilter } from 'lucide-react';

type ButtonColor =
  | 'blue'
  | 'red'
  | 'orange'
  | 'green'
  | 'primary'
  | 'secondary'
  | 'danger'
  | 'warning'
  | 'info'
  | undefined;

interface DefaultButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  loading?: boolean;
  size?: 'small' | 'medium';
  rounded?: 'rounded' | 'rounded-md' | 'rounded-lg' | 'rounded-full';
  color?: ButtonColor;
}

export function DefaultButton({
  children,
  loading,
  size = 'small',
  rounded = 'rounded',
  color = 'blue',
  ...props
}: React.PropsWithChildren<DefaultButtonProps>) {
  return (
    <Button
      {...props}
      loading={loading}
      color={color}
      size={size}
      className={rounded + ' w-fit rounded-xl ' + props.className}
    >
      {children}
    </Button>
  );
}

export function FilterButton({ children, ...props }: Omit<DefaultButtonProps, 'mood'>) {
  return (
    <DefaultButton {...props} type="button">
      <span className="flex items-center gap-x-2">
        <LucideFilter size={16} />
        {children || <span className="sr-only">Filter</span>}
      </span>
    </DefaultButton>
  );
}

export function DownloadButton({ children, ...props }: Omit<DefaultButtonProps, 'mood'>) {
  return (
    <DefaultButton {...props} type="button">
      <span className="flex items-center gap-x-2">
        <LucideDownload size={16} />
        {children || <span className="sr-only">Download</span>}
      </span>
    </DefaultButton>
  );
}

export function SecondaryButton({ children, ...props }: Omit<DefaultButtonProps, 'mood'>) {
  return (
    <DefaultButton
      {...props}
      type={props.type ?? 'button'}
      className="!bg-white !hover:bg-white/90 border !border-slate-400 !text-slate-500"
    >
      <span className="flex items-center gap-x-2">{children || 'Cancel'}</span>
    </DefaultButton>
  );
}

export function GenerateButton({ children, ...props }: Omit<DefaultButtonProps, 'mood'>) {
  return (
    <DefaultButton
      {...props}
      type={props.type ?? 'button'}
      className="!bg-white !hover:bg-white/90 border !border-slate-400 !text-primary"
    >
      <span className="flex items-center gap-x-2">{children || 'Cancel'}</span>
    </DefaultButton>
  );
}

export function AddButton({ children, ...props }: Omit<DefaultButtonProps, 'mood'>) {
  return (
    <DefaultButton {...props} type={props.type ?? 'submit'} className="!bg-black !text-white !hover:bg-gray-800">
      <span className="flex items-center gap-x-2">{children || 'Submit'}</span>
    </DefaultButton>
  );
}

export function SubmitButton({ children, ...props }: Omit<DefaultButtonProps, 'mood'>) {
  return (
    <DefaultButton {...props} type={props.type ?? 'submit'} className="bg-primary hover:bg-primary/90">
      <span className="flex items-center gap-x-2">{children || 'Submit'}</span>
    </DefaultButton>
  );
}

export function SubmitButton2({ children, ...props }: Omit<DefaultButtonProps, 'mood'>) {
  return (
    <DefaultButton {...props} type={props.type ?? 'submit'} className="bg-[#2A61CF]">
      {children || 'Submit'}
    </DefaultButton>
  );
}
